import { trackedArray, trackedSet } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import type {
  SmartInsightDay,
  SmartInsightsHotel,
  SmartInsightsPropertyTagsUser,
} from 'lumen/models';
import type { SmartInsightDayResponseMeta } from 'lumen/serializers/smart-insights/smart-insight-day';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class PortfolioSmartInsightsService extends Service {
  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];

  @tracked allSmartInsightsHotels: SmartInsightsHotel[] = [];
  @tracked currentHotelNumber = 1;
  @tracked expandedHotelSmartInsightsDay: SmartInsightDay | null = null;
  @tracked maxFetchingHotelsCount = 0;
  @tracked selectedDormIds: string[] = [];
  @tracked selectedRegionalVpIds: string[] = [];
  @tracked selectedSmartInsightDay: SmartInsightDay | null = null;
  @tracked smartInsightDays = trackedArray<SmartInsightDay>();
  @tracked smartInsightsHotelIDsToDisplay = trackedSet<string>();
  @tracked smartInsightsHotelIDsWithFetchedInsights = trackedSet<string>();
  @tracked smartInsightsHotelIDsWithFetchErrors = trackedSet<string>();

  getHotelSmartInsightsTask = task(
    { enqueue: true },
    async (hotel: SmartInsightsHotel) => {
      const today = dayjs().startOf('day');

      // Important: when adjusting QPs here, it's possible that the source QPs won't match anymore in the SmartInsightDayInsightEntry model.
      // They need to be kept aligned to ensure we can navigate to the correct source for insights.
      const requestParams = {
        namespace: APIGATEWAY,
        typeKey: 'smartinsights/insights',
        subscription_id: hotel.subscriptionId,
        extract_date: today.format('YYYY-MM-DD'),
        from_date_start: today.format('YYYY-MM-DD'),
        rate_type: 'lowest',
        ota: 'bookingdotcom',
        // Not used by the backend — included solely to bust the browser cache when the user
        // changes their in-app language. Without this, the cached response (in the old language)
        // would be served instead of fetching fresh translated insights.
        language: this.hotelAndMarketService.user.locale,
      };

      try {
        const MAX_POLLING_ATTEMPTS = 24;

        for (let attempt = 0; attempt < MAX_POLLING_ATTEMPTS; attempt++) {
          // Spread requestParams on each iteration: handleQuery mutates the object by deleting namespace/typeKey.
          const response = await this.store.query<SmartInsightDay>(
            'smart-insights/smart-insight-day',
            { ...requestParams },
          );

          const meta = response.meta as SmartInsightDayResponseMeta;

          if (meta?.isFailed) {
            throw new Error('Smart insights generation failed');
          }

          if (meta?.isGenerating) {
            await timeout((meta.retryAfterSeconds ?? 5) * 1000);
            continue;
          }

          // Set the hotel relationship on every fetched SmartInsightDay, so we can aggregate insights by hotel later.
          response.forEach((insightDay) => {
            insightDay.hotelinfoId = hotel.hotelinfoId;
            insightDay.meta = meta;
          });

          // Add the subscription ID to the set of fetched insights hotel IDs.
          this.smartInsightsHotelIDsWithFetchedInsights.add(hotel.hotelinfoId);
          this.smartInsightsHotelIDsWithFetchErrors.delete(hotel.hotelinfoId);

          // Direct mutation triggers granular reactivity - only new items render
          this.smartInsightDays.push(...response);

          return response;
        }

        throw new Error('Smart insights polling exceeded maximum attempts');
      } catch (error) {
        this.smartInsightsHotelIDsWithFetchErrors.add(hotel.hotelinfoId);
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      } finally {
        this.currentHotelNumber += 1;
      }
    },
  );

  getMyHotelsTask = task({ restartable: true }, async () => {
    const requestParams = {
      namespace: 'api/v3',
      typeKey: 'smart_insight_multi_property_hotels',
      market_id: this.hotelAndMarketService.market?.id,
    };

    try {
      const response = await this.store.query<SmartInsightsHotel>(
        'smart-insights/hotel',
        requestParams,
      );

      // Keep hotels that aren't on the performance plan (`hasSiMpv === false`): they're
      // shown in the property filter as disabled, upsell options rather than hidden.
      // Hotels with no Smart Insights access at all are a different, unrelated product
      // tier and stay hidden entirely (pre-existing behavior).
      this.allSmartInsightsHotels = this.deduplicateHotelsByName(
        response
          .filter((hotel) => hotel.hasAccessSmartInsights)
          .toSorted((a, b) => a.hotelinfoName.localeCompare(b.hotelinfoName)),
      );

      // If no Regional VP or DORM filters are selected, default to selecting all.
      if (this.selectedRegionalVpIds.length === 0) {
        this.selectedRegionalVpIds = this.regionalVps
          .filter((vp) => vp.id !== null)
          .map((vp) => vp.id!);
      }

      if (this.selectedDormIds.length === 0) {
        this.selectedDormIds = this.dorms
          .filter((dorm) => dorm.id !== null)
          .map((dorm) => dorm.id!);
      }

      // If no hotels are selected to be displayed, default to selecting all hotels
      // on the performance plan. Hotels not on the performance plan are disabled in
      // the filter and must never be auto-selected or fetched.
      if (this.smartInsightsHotelIDsToDisplay.size === 0) {
        const defaultSelectionOfHotels = this.allSmartInsightsHotels
          .filter((hotel) => hotel.hasSiMpv)
          .map((hotel) => hotel.hotelinfoId);

        this.smartInsightsHotelIDsToDisplay = trackedSet(
          defaultSelectionOfHotels,
        );

        void this.startMarketFetchSmartInsightsQueue(
          Array.from(this.smartInsightsHotelIDsToDisplay),
        );
      } else {
        this.fetchSmartInsightsForSelectedHotels();
      }

      return response;
    } catch (error) {
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dorms() {
    const { allSmartInsightsHotels } = this;

    const dormSet = new Set<SmartInsightsPropertyTagsUser>();
    allSmartInsightsHotels.forEach((hotel) => {
      if (hotel.propertyTags.dorm) {
        dormSet.add(hotel.propertyTags.dorm);
      }
    });

    return Array.from(dormSet);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredSmartInsightsHotels() {
    return this.allSmartInsightsHotels.filter((hotel) => {
      const dormMatch =
        this.selectedDormIds.length === 0 ||
        this.selectedDormIds.includes(hotel.propertyTags.dorm?.id ?? '');

      const regionalVpMatch =
        this.selectedRegionalVpIds.length === 0 ||
        this.selectedRegionalVpIds.includes(
          hotel.propertyTags.regionalVp?.id ?? '',
        );

      return dormMatch && regionalVpMatch;
    });
  }

  get hotelsByHotelinfoId(): Map<string, SmartInsightsHotel> {
    const map = new Map<string, SmartInsightsHotel>();
    this.allSmartInsightsHotels.forEach((hotel) => {
      map.set(hotel.hotelinfoId, hotel);
    });
    return map;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get leadtime() {
    if (this.access.hasSmartInsightsYearView) {
      return 365;
    }

    return 90;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get regionalVps() {
    const { allSmartInsightsHotels } = this;

    const regionalVpSet = new Set<SmartInsightsPropertyTagsUser>();
    allSmartInsightsHotels.forEach((hotel) => {
      if (hotel.propertyTags.regionalVp) {
        regionalVpSet.add(hotel.propertyTags.regionalVp);
      }
    });

    return Array.from(regionalVpSet);
  }

  /**
   * Cancels all running tasks. Should be called when navigating away from Smart Insights
   * to prevent API requests from continuing in the background.
   */
  cancelAllTasks(): void {
    void this.getHotelSmartInsightsTask.cancelAll();
    void this.getMyHotelsTask.cancelAll();
  }

  deduplicateHotelsByName(hotels: SmartInsightsHotel[]): SmartInsightsHotel[] {
    const uniqueHotels = new Map<string, SmartInsightsHotel>();

    hotels.forEach((hotel) => {
      const key = hotel.hotelinfoName;
      const existing = uniqueHotels.get(key);

      if (!existing) {
        uniqueHotels.set(key, hotel);
        return;
      }

      // Prefer the entry that is on the performance plan, so a disabled (upsell)
      // duplicate can never hide a hotel the user can actually select.
      if (hotel.hasSiMpv !== existing.hasSiMpv) {
        if (hotel.hasSiMpv) {
          uniqueHotels.set(key, hotel);
        }
        return;
      }

      // Otherwise, prefer the entry that has property tags.
      const hasTags =
        hotel.propertyTags.dorm !== null ||
        hotel.propertyTags.regionalVp !== null;
      const existingHasTags =
        existing.propertyTags.dorm !== null ||
        existing.propertyTags.regionalVp !== null;

      if (hasTags && !existingHasTags) {
        uniqueHotels.set(key, hotel);
      }
    });

    return Array.from(uniqueHotels.values());
  }

  fetchSmartInsightsForSelectedHotels(): void {
    // Filter out hotels that have already fetched insights.
    const hotelsWithoutFetchedInsights = Array.from(
      this.smartInsightsHotelIDsToDisplay,
    ).filter((id) => !this.smartInsightsHotelIDsWithFetchedInsights.has(id));

    if (hotelsWithoutFetchedInsights.length > 0) {
      // Start fetching insights for hotels that haven't been fetched yet.
      void this.startMarketFetchSmartInsightsQueue(
        hotelsWithoutFetchedInsights,
      );
    }
  }

  setSelectedDormIds(dormIds: string[]): void {
    this.selectedDormIds = dormIds;

    this.syncHotelIDsToDisplayWithSelectedFilters();

    // Cancel existing queue just in case all new hotels have already been fetched.
    void this.getHotelSmartInsightsTask.cancelAll();

    this.fetchSmartInsightsForSelectedHotels();
  }

  setSelectedRegionalVpIds(regionalVpIds: string[]): void {
    this.selectedRegionalVpIds = regionalVpIds;

    this.syncHotelIDsToDisplayWithSelectedFilters();

    // Cancel existing queue just in case all new hotels have already been fetched.
    void this.getHotelSmartInsightsTask.cancelAll();

    this.fetchSmartInsightsForSelectedHotels();
  }

  setSelectedSmartInsightDay(smartInsightDay: SmartInsightDay | null): void {
    this.selectedSmartInsightDay = smartInsightDay;
  }

  setSmartInsightsHotelIDsToDisplay(hotelinfoIds: string[]): void {
    this.smartInsightsHotelIDsToDisplay = trackedSet(hotelinfoIds);
    // Cancel existing queue just in case all new hotels have already been fetched.
    void this.getHotelSmartInsightsTask.cancelAll();
    this.fetchSmartInsightsForSelectedHotels();
  }

  async startMarketFetchSmartInsightsQueue(
    hotelinfoIds: string[],
  ): Promise<void> {
    // Await the cancellation, otherwise the `finally` block in the last `getHotelSmartInsightsTask` might still run after starting new tasks.
    // This is to avoid a bug where the `currentHotelNumber` gets incremented incorrectly due to overlapping tasks.
    await this.getHotelSmartInsightsTask.cancelAll();

    // Calculate how many hotels have already been fetched from the selected set
    const alreadyFetchedCount = Array.from(
      this.smartInsightsHotelIDsToDisplay,
    ).filter((id) =>
      this.smartInsightsHotelIDsWithFetchedInsights.has(id),
    ).length;

    // Start counting from where we left off, and use the total selected hotels as max
    this.currentHotelNumber = alreadyFetchedCount + 1;
    this.maxFetchingHotelsCount = this.smartInsightsHotelIDsToDisplay.size;

    const sortedHotelinfoIds = hotelinfoIds.toSorted((a, b) => {
      const hotelA = this.hotelsByHotelinfoId.get(a);
      const hotelB = this.hotelsByHotelinfoId.get(b);
      return (hotelA?.hotelName ?? '').localeCompare(hotelB?.hotelName ?? '');
    });

    sortedHotelinfoIds.forEach((hotelinfoId) => {
      const hotel = this.hotelsByHotelinfoId.get(hotelinfoId);

      if (!hotel) {
        return;
      }

      void this.getHotelSmartInsightsTask.perform(hotel);
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  syncHotelIDsToDisplayWithSelectedFilters() {
    const newSmartInsightsHotelIDsToDisplay = trackedSet(
      Array.from(this.smartInsightsHotelIDsToDisplay).filter((hotelId) => {
        const hotel = this.allSmartInsightsHotels.find(
          (h) => h.hotelinfoId === hotelId,
        );
        if (!hotel) {
          return false;
        }
        const regionalVpMatch =
          this.selectedRegionalVpIds.length === 0 ||
          this.selectedRegionalVpIds.includes(
            hotel.propertyTags.regionalVp?.id ?? '',
          );

        const dormMatch =
          this.selectedDormIds.length === 0 ||
          this.selectedDormIds.includes(hotel.propertyTags.dorm?.id ?? '');

        return regionalVpMatch && dormMatch;
      }),
    );

    this.smartInsightsHotelIDsToDisplay = newSmartInsightsHotelIDsToDisplay;
  }
}

declare module '@ember/service' {
  interface Registry {
    'portfolio-smart-insights': PortfolioSmartInsightsService;
  }
}
